'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import Checkbox from '../ui/Checkbox';

interface FilterDropdownProps {
    label: string;
    filters: {
        id: string;
        label: string;
    }[];
    initialSelectedFilters?: string[];
    onFilterChange: (filterId: string) => void;
    onClearAll: () => void;
    asInput?: boolean;
    truncate?: boolean;
}

const FilterDropdown = (props: FilterDropdownProps) => {
    const {
        label,
        filters,
        onFilterChange,
        onClearAll,
        initialSelectedFilters,
        asInput,
        truncate,
    } = props;
    const t = useTranslations();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<string[]>(initialSelectedFilters || []);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

    const handleFilterChange = (filterId: string) => {
        onFilterChange(filterId);
        setSelectedFilters((prevFilters) => {
            if (prevFilters.includes(filterId)) {
                return prevFilters.filter((id) => id !== filterId);
            }
            return [...prevFilters, filterId];
        });
    };

    const handleClearAll = () => {
        setSelectedFilters([]);
        setIsDropdownOpen(false);
        onClearAll();
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsDropdownOpen(false);
        }
    };

    const inputClassNames =
        'flex gap-2.5 justify-between items-center h-[72px] w-full border py-5 px-6 lg:text-h5 max-lg:text-h6 bg-transparent rounded-none uppercase leading-none font-normal text-white placeholder-white/50 focus:border-border-white focus:outline-none border-white placeholder-shown:border-white newsletter-input';
    const labelClassNames = `flex gap-2.5 max-lg:text-[22px] items-center text-2xl uppercase p-1 border-b hover:text-primary hover:border-primary max-lg:w-full max-lg:justify-between transition-colors duration-300 ${isDropdownOpen ? 'text-primary border-primary' : 'text-white border-white'}`;
    const triggerClassNames = asInput ? inputClassNames : labelClassNames;

    useEffect(() => {
        const body = document.body;

        const getScrollbarWidth = () => {
            return window.innerWidth - document.documentElement.clientWidth;
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            body.setAttribute('data-lenis-prevent', 'true');
            const scrollbarWidth = getScrollbarWidth();
            body.style.paddingRight = `${scrollbarWidth}px`;
            body.classList.add('overflow-hidden', 'mobile-menu-open');
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
            body.removeAttribute('data-lenis-prevent');
            body.classList.remove('overflow-hidden', 'mobile-menu-open');
            body.style.paddingRight = '';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            body.removeAttribute('data-lenis-prevent');
            body.classList.remove('overflow-hidden', 'mobile-menu-open');
            body.style.paddingRight = '';
        };
    }, [isDropdownOpen]);

    return (
        <div className="relative max-lg:w-full" ref={dropdownRef}>
            <button className={triggerClassNames} onClick={toggleDropdown} type="button">
                {label}
                <svg
                    width="13"
                    height="8"
                    viewBox="0 0 13 8"
                    fill="none"
                    stroke="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: `rotate(${isDropdownOpen ? '0' : '180deg'})` }}
                    className={`transform transition-transform duration-300`}
                >
                    <path d="M12 6.5L6.5 1.5L1 6.5" strokeWidth="2" />
                </svg>
            </button>
            {isDropdownOpen && (
                <div className="lg:absolute lg:top-[calc(100%+16px)] lg:left-0 z-20 bg-black">
                    <div className="lg:min-w-[10rem] max-lg:px-0 lg:max-w-[600px] lg:w-max p-5 flex flex-col lg:gap-7 gap-3 lg:border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 lg:max-h-[50vh] max-h-[200px] overflow-auto">
                            {filters
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((filter) => (
                                    <Checkbox
                                        key={filter.id}
                                        label={filter.label}
                                        onChange={() => handleFilterChange(filter.id)}
                                        checked={selectedFilters.includes(filter.id)}
                                        truncate={truncate}
                                        wrap
                                    />
                                ))}
                        </div>
                        <hr />
                        <button
                            onClick={handleClearAll}
                            className="w-fit px-3 py-0.5 border border-primary uppercase text-primary text-2xl leading-8"
                            type="button"
                        >
                            {t('Common.clearAll')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
